import React from "react";
import { Users, Video, Clock, Calendar, MessageCircle, AlertCircle, CalendarPlus } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import ShareMenu from "./ShareMenu";
import { downloadWeeklyIcs } from "../lib/icsCalendar";

const CLINIC_WA_E164 = "+447445372190";

/** Weekly Online Ruqyah Group — a two-card poster on the Ruqyah page.
 *  Women-only 4 PM (UK) + Men-only 6 PM (UK) every Saturday. £25/person.
 *  Registration required 2–3 days in advance via WhatsApp (places limited).
 *  Uses WhatsApp Video Call, Zoom or Teams — details confirmed on booking. */
const OnlineRuqyahGroupCard = () => {
  const { t } = useLanguage();

  const buildWa = (msg) =>
    `https://wa.me/${CLINIC_WA_E164.replace("+", "")}?text=${encodeURIComponent(msg)}`;

  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}/ruqyah#online-group`
    : "";

  const sessions = [
    {
      id: "women",
      accent: "#8C3A3A",
      pill: t("orgroup_women_pill"),
      title: t("orgroup_women_title"),
      time: t("orgroup_women_time"),
      waMsg: t("orgroup_women_wa"),
      ukHour: 16,  // 4 PM UK time
      icsTitle: "Online Ruqyah Group — Women only",
    },
    {
      id: "men",
      accent: "#2C5545",
      pill: t("orgroup_men_pill"),
      title: t("orgroup_men_title"),
      time: t("orgroup_men_time"),
      waMsg: t("orgroup_men_wa"),
      ukHour: 18,  // 6 PM UK time
      icsTitle: "Online Ruqyah Group — Men only",
    },
  ];

  const addToCalendar = (s) =>
    downloadWeeklyIcs({
      title: s.icsTitle,
      description:
        "Weekly online Ruqyah group by Islamic Cure & Care.\n" +
        "£25 per person. Book via WhatsApp: +447445372190.\n" +
        "Meeting link sent by WhatsApp after registration.",
      location: "Online — WhatsApp Video Call / Zoom / Teams",
      weekday: 6, // Saturday
      ukHour: s.ukHour,
      ukMinute: 0,
      durationHrs: 1,
      remindMins: 60,
      filename: `${s.id}-ruqyah-group.ics`,
    });

  return (
    <section
      id="online-group"
      data-testid="online-ruqyah-group"
      className="mb-16 rounded-3xl overflow-hidden border border-gold/40 bg-gradient-to-br from-forest via-[#1F4030] to-[#0F2A1F] text-sand shadow-xl"
    >
      <div className="p-6 sm:p-8">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.3em] text-gold font-semibold mb-2 flex items-center gap-2">
              <Calendar className="w-4 h-4" /> {t("orgroup_pill")}
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold leading-tight mb-2">
              {t("orgroup_title")}
            </h2>
            <p className="text-sand/80 text-sm leading-relaxed max-w-2xl">
              {t("orgroup_subtitle")}
            </p>
          </div>
          <ShareMenu title={t("orgroup_title")} url={shareUrl} compact />
        </div>

        {/* Two sessions */}
        <div className="grid md:grid-cols-2 gap-4 mb-5">
          {sessions.map((s) => (
            <article
              key={s.id}
              data-testid={`orgroup-session-${s.id}`}
              className="bg-sand/8 backdrop-blur rounded-2xl p-5 border border-gold/25 hover:border-gold/50 transition"
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: `${s.accent}30`, color: "#F5D992" }}
                >
                  <Users className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-gold/90 font-semibold">
                    {s.pill}
                  </p>
                  <h3 className="font-heading text-xl font-semibold leading-snug">
                    {s.title}
                  </h3>
                </div>
              </div>

              <ul className="space-y-1.5 text-sm text-sand/90 mb-4">
                <li className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gold flex-shrink-0" /> {s.time}
                </li>
                <li className="flex items-center gap-2">
                  <Video className="w-4 h-4 text-gold flex-shrink-0" /> {t("orgroup_platform")}
                </li>
                <li className="text-gold font-semibold">£25 · {t("orgroup_per_person")}</li>
              </ul>

              <a
                data-testid={`orgroup-book-${s.id}`}
                href={buildWa(s.waMsg)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition w-full"
              >
                <MessageCircle className="w-4 h-4" /> {t("orgroup_book_cta")}
              </a>

              {/* Optional — customer can add the session to their own calendar */}
              <button
                type="button"
                onClick={() => addToCalendar(s)}
                data-testid={`orgroup-calendar-${s.id}`}
                className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-sand/12 hover:bg-sand/22 text-sand border border-sand/25 rounded-full px-4 py-2 text-xs font-medium transition"
              >
                <CalendarPlus className="w-3.5 h-3.5" /> {t("add_to_calendar")}
              </button>
            </article>
          ))}
        </div>

        {/* Registration notice */}
        <div className="flex items-start gap-2 bg-gold/15 rounded-xl p-3 border border-gold/30">
          <AlertCircle className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
          <p className="text-xs text-sand/95 leading-relaxed">
            {t("orgroup_register_notice")}
            <br />
            <span className="text-sand/70 italic">{t("add_to_calendar_note")}</span>
          </p>
        </div>
      </div>
    </section>
  );
};

export default OnlineRuqyahGroupCard;
